/**
 * 起動後同期の一部として、wstHYPE の送受金を含む HyperEVM の取引を一度だけ再解析対象に戻す。
 * wstHYPE の Transfer は stHYPE の Transfer の鏡像で二重計上になっていたため、デコード時に除外するよう修正した。
 * 修正前に解析済みの取引を、この処理で解析し直させる。
 */

import type { RunToDatabase } from '@/types'
import {
  getSyncTimestamp,
  resetEvmTxAnalyzedStatusByErc20Token,
  upsertSyncTimestamp,
} from '@pkg/database-portfolio'

/** 再解析を一度だけ実行したことを記録する sync timestamp の key */
export const reanalyzeWstHypeEvmTxsKey = 'reanalyze-wsthype-mirror-evm-tx'

/** HyperEVM の chain id */
export const wstHypeChainId = 999

/** wstHYPE (Staked HYPE Shares) のトークンアドレス */
export const wstHypeTokenAddress = '0x94e8396e0869c9f2200760af0621afd240e1cf38'

/** 再解析の完了日時を読み込む関数 */
type LoadCompletedAt = (params: {
  readonly readFromDatabase: RunToDatabase
  readonly key: string
}) => Promise<number>

/** 指定した ERC20 トークンの送受金を含む取引を再解析対象に戻す関数 */
type ResetEvmTxsByErc20Token = (params: {
  readonly writeToDatabase: RunToDatabase
  readonly chainId: number
  readonly tokenAddress: typeof wstHypeTokenAddress
}) => Promise<number>

/** 再解析の完了日時を保存する関数 */
type SaveCompletedAt = (params: {
  readonly writeToDatabase: RunToDatabase
  readonly key: string
  readonly timestamp: number
}) => Promise<void>

/**
 * sync timestamp テーブルから再解析の完了日時を読み込む。
 * @param params DB 読み込み関数と key を受け取る。
 * @returns 完了日時 (UNIX ミリ秒)。未実行の場合は 0 を返す。
 */
const loadCompletedAtFromDatabase: LoadCompletedAt = ({
  readFromDatabase,
  key,
}) => readFromDatabase((database) => getSyncTimestamp({ database, key }))

/**
 * 指定した ERC20 トークンの送受金を含む取引を再解析対象に戻す。
 * @param params DB 書き込み関数、chain id、トークンアドレスを受け取る。
 * @returns 再解析対象に戻した取引の件数を返す。
 */
const resetEvmTxsByErc20TokenInDatabase: ResetEvmTxsByErc20Token = ({
  writeToDatabase,
  chainId,
  tokenAddress,
}) =>
  writeToDatabase((database) =>
    resetEvmTxAnalyzedStatusByErc20Token({ database, chainId, tokenAddress }),
  )

/**
 * sync timestamp テーブルに再解析の完了日時を保存する。
 * @param params DB 書き込み関数、key、完了日時を受け取る。
 * @returns 値は返さない。
 */
const saveCompletedAtToDatabase: SaveCompletedAt = ({
  writeToDatabase,
  key,
  timestamp,
}) =>
  writeToDatabase((database) =>
    upsertSyncTimestamp({ database, key, timestamp }),
  )

/**
 * wstHYPE の送受金を含む解析済みの HyperEVM 取引を、一度だけ再解析対象に戻す。
 * - 再解析では wstHYPE の鏡像 Transfer が除外され、stHYPE のみで計上し直される
 * - lock 済みの取引は同期処理側で再解析から除外される
 * - 再解析対象に戻してから完了日時を保存するので、途中で失敗した場合は次回の同期で再試行される
 * @param params DB 読み書き関数と、必要に応じて差し替える入出力関数・現在時刻取得関数を受け取る。
 * @returns 値は返さない。
 */
export const reanalyzeWstHypeEvmTxsOnce = async ({
  readFromDatabase,
  writeToDatabase,
  loadCompletedAt = loadCompletedAtFromDatabase,
  resetEvmTxsByErc20Token = resetEvmTxsByErc20TokenInDatabase,
  saveCompletedAt = saveCompletedAtToDatabase,
  now = Date.now,
}: {
  readonly readFromDatabase: RunToDatabase
  readonly writeToDatabase: RunToDatabase
  readonly loadCompletedAt?: LoadCompletedAt
  readonly resetEvmTxsByErc20Token?: ResetEvmTxsByErc20Token
  readonly saveCompletedAt?: SaveCompletedAt
  readonly now?: () => number
}): Promise<void> => {
  const completedAt = await loadCompletedAt({
    readFromDatabase,
    key: reanalyzeWstHypeEvmTxsKey,
  })
  if (completedAt > 0) {
    return
  }

  await resetEvmTxsByErc20Token({
    writeToDatabase,
    chainId: wstHypeChainId,
    tokenAddress: wstHypeTokenAddress,
  })
  await saveCompletedAt({
    writeToDatabase,
    key: reanalyzeWstHypeEvmTxsKey,
    timestamp: now(),
  })
}
